// FO-320 (batch-36) — kostendragers: het doeltype van een inkoop-kostentoewijzing
// is generiek (medewerker / voertuig / kar-koffer). Leaf-module zonder eigen
// Zod/waarde-imports, zodat zowel `inkoop-toewijzing` als `employee-kosten` erop
// kunnen leunen zonder circulaire import.

// ─── Doeltype (spiegelt de Prisma-enum ToewijzingDoeltype) ───────────────────

// batch-65 inkoop-kosten (FO-483) — KLANT, WERKBON en PROJECT erbij: kosten die
// bij een klant horen (toerekenen), los van de interne dragers. De volledige lijst
// spiegelt de Prisma-enum; interne overzichten gebruiken KOSTENDRAGER_DOELTYPE.
export const TOEWIJZING_DOELTYPE = ['MEDEWERKER', 'VOERTUIG', 'DRAGER', 'KLANT', 'WERKBON', 'PROJECT'] as const
export type ToewijzingDoeltype = (typeof TOEWIJZING_DOELTYPE)[number]

export const TOEWIJZING_DOELTYPE_LABELS: Record<ToewijzingDoeltype, string> = {
  MEDEWERKER: 'Medewerker',
  VOERTUIG: 'Voertuig',
  DRAGER: 'Kar/koffer',
  KLANT: 'Klant',
  WERKBON: 'Werkbon',
  PROJECT: 'Project',
}

/** Meervoud-labels voor de dragertype-dimensie in overzichten/trends. */
export const TOEWIJZING_DOELTYPE_MEERVOUD: Record<ToewijzingDoeltype, string> = {
  MEDEWERKER: 'Medewerkers',
  VOERTUIG: 'Voertuigen',
  DRAGER: 'Karren/koffers',
  KLANT: 'Klanten',
  WERKBON: 'Werkbonnen',
  PROJECT: 'Projecten',
}

// ─── Interne kostendragers vs klant-toerekening (batch-65, FO-483) ───────────
//
// Twee dimensies op dezelfde regel: de INTERNE drager (medewerker / voertuig /
// kar-koffer: waar de kost binnen STREEP telt) en de KLANT-toerekening (klant /
// werkbon / project: bij welke klant de kost hoort, basis voor doorbelasten en
// marge). Een regel mag op beide hangen (spec 183 §3.3); per dimensie geldt
// "som <= regelbedrag", de UI waarschuwt als intern + klant samen meer is.

/** De interne kostendragers (FO-320): medewerker, voertuig en kar/koffer. */
export const KOSTENDRAGER_DOELTYPE = ['MEDEWERKER', 'VOERTUIG', 'DRAGER'] as const
export type KostendragerDoeltype = (typeof KOSTENDRAGER_DOELTYPE)[number]

/** De klant-toerekening (FO-483): klant, optioneel verfijnd naar werkbon of project. */
export const KLANT_TOEWIJZING_DOELTYPE = ['KLANT', 'WERKBON', 'PROJECT'] as const
export type KlantToewijzingDoeltype = (typeof KLANT_TOEWIJZING_DOELTYPE)[number]

export function isKlantToewijzingDoeltype(d: ToewijzingDoeltype | string): d is KlantToewijzingDoeltype {
  return d === 'KLANT' || d === 'WERKBON' || d === 'PROJECT'
}

export function isKostendragerDoeltype(d: ToewijzingDoeltype | string): d is KostendragerDoeltype {
  return d === 'MEDEWERKER' || d === 'VOERTUIG' || d === 'DRAGER'
}

// ─── Kostensoort-relevantie per doeltype ─────────────────────────────────────
//
// batch-65 inkoop-kosten (FO-482) — de code-map KOSTENSOORT_RELEVANT_VOOR_DOELTYPE
// die hier stond is vervallen. Hij bestond omdat de kostensoort een enum was en
// geen stamtabel; sinds FO-482 is het veld `relevantVoorDoeltype[]` op de rij in de
// stamtabel de waarheid (beheerbaar in Instellingen > Kosten). De keuzelijst
// sorteert via `kostensoortKeuzeOpties` / `kostensoortRelevantieVoorDoeltype`
// (kostensoorten.ts); niets wordt geblokkeerd. De klant-doeltypen (FO-483) delen
// de relevantie KLANT.
